import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * useGateToken
 * ============
 *
 * Persist a short-lived "DOB gate" JWT in sessionStorage, scoped to a
 * single invoice hash. The token is what unlocks the saved-card
 * management endpoints on the public invoice page; we cache it for the
 * tab session so a patient who briefly navigates away and comes back
 * doesn't have to re-enter their DOB inside the 30-minute window.
 *
 * Why sessionStorage (not localStorage):
 *   - sessionStorage is wiped when the tab closes — we never want a
 *     gate token to outlive the visit. localStorage would persist
 *     across browser restarts, which is the wrong tradeoff for a
 *     credential that authorizes payment-method mutations.
 *
 * Why per-hash key:
 *   - Two invoices opened in two tabs to two different patients in the
 *     same household must not share a gate token; the publicHash is the
 *     natural namespace.
 *
 * Returns:
 *   {
 *     gateToken: string | null,
 *     setGateToken: (token | null) => void,    // setting null clears it
 *     gateMeta:  { firstName, hasCards } | null,
 *     setGateMeta: ...,
 *   }
 *
 * Expiry:
 *   We don't try to inspect the JWT exp client-side. Server returns
 *   401/expired=true on a stale token; callers clear it via
 *   `setGateToken(null)` and re-prompt for DOB.
 */
export default function useGateToken(publicHash) {
    const tokenKey = useMemo(() => `gateToken:${publicHash || ''}`, [publicHash]);
    const metaKey = useMemo(() => `gateMeta:${publicHash || ''}`, [publicHash]);

    const [gateToken, setTokenState] = useState(() => {
        if (!publicHash || typeof window === 'undefined') return null;
        try { return window.sessionStorage.getItem(tokenKey) || null; }
        catch { return null; }
    });

    const [gateMeta, setMetaState] = useState(() => {
        if (!publicHash || typeof window === 'undefined') return null;
        try {
            const raw = window.sessionStorage.getItem(metaKey);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    });

    const setGateToken = useCallback((next) => {
        setTokenState(next || null);
        if (typeof window === 'undefined') return;
        try {
            if (next) window.sessionStorage.setItem(tokenKey, next);
            else window.sessionStorage.removeItem(tokenKey);
        } catch {}
    }, [tokenKey]);

    const setGateMeta = useCallback((next) => {
        setMetaState(next || null);
        if (typeof window === 'undefined') return;
        try {
            if (next) window.sessionStorage.setItem(metaKey, JSON.stringify(next));
            else window.sessionStorage.removeItem(metaKey);
        } catch {}
    }, [metaKey]);

    // Keep state in sync if the hash changes mid-session (rare, but the
    // route can switch when staff navigates between invoices in dev).
    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            setTokenState(window.sessionStorage.getItem(tokenKey) || null);
            const raw = window.sessionStorage.getItem(metaKey);
            setMetaState(raw ? JSON.parse(raw) : null);
        } catch {}
    }, [tokenKey, metaKey]);

    return { gateToken, setGateToken, gateMeta, setGateMeta };
}
